import type {
  BehaviourEvent,
  CheckoutRequest,
  CheckoutResponse,
  CreateTextEntry,
  EntryDetail,
  EntryListResponse,
  EntrySearchResponse,
  EntrySummary,
  MeResponse,
  PortalResponse,
  PracticeDetail,
  PracticesListResponse,
  PracticeSummary,
  ProfileResponse,
  RespondPractice,
  StartPractice,
  SubmitCorrection,
} from '@resonance/shared-types';

const base = import.meta.env.VITE_API_URL || '';

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (!res.ok) throw new Error(`api ${path} -> ${res.status}`);
  return (await res.json()) as T;
}

export const api = {
  me: () => call<MeResponse>('/api/me'),
  checkout: (body: CheckoutRequest) =>
    call<CheckoutResponse>('/api/billing/checkout', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  portal: () => call<PortalResponse>('/api/billing/portal', { method: 'POST' }),

  // Entries
  createTextEntry: (body: CreateTextEntry) =>
    call<EntrySummary>('/api/entries', { method: 'POST', body: JSON.stringify(body) }),
  uploadVoice: async (audio: Blob, durationSeconds: number): Promise<EntrySummary> => {
    const res = await fetch(`${base}/api/entries/voice`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'content-type': audio.type || 'audio/webm',
        'x-duration-seconds': String(Math.round(durationSeconds)),
      },
      body: audio,
    });
    if (!res.ok) throw new Error(`api /api/entries/voice -> ${res.status}`);
    return (await res.json()) as EntrySummary;
  },
  listEntries: (before?: number) =>
    call<EntryListResponse>(`/api/entries${before ? `?before=${before}` : ''}`),
  searchEntries: (q: string) =>
    call<EntrySearchResponse>(`/api/entries/search?q=${encodeURIComponent(q)}`),
  getEntry: (id: string) => call<EntryDetail>(`/api/entries/${id}`),

  // Profile
  profile: () => call<ProfileResponse>('/api/profile'),
  submitCorrection: (body: SubmitCorrection) =>
    call<{ ok: true }>('/api/profile/corrections', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  regenerateProfile: () =>
    call<{ version: number }>('/api/profile/regenerate', { method: 'POST' }),

  // Events
  recordEvent: (body: BehaviourEvent) =>
    call<{ ok: true }>('/api/events', { method: 'POST', body: JSON.stringify(body) }),

  // Practices
  listPractices: () => call<PracticesListResponse>('/api/practices'),
  startPractice: (body: StartPractice) =>
    call<PracticeSummary>('/api/practices', { method: 'POST', body: JSON.stringify(body) }),
  getPractice: (id: string) => call<PracticeDetail>(`/api/practices/${id}`),
  respondPractice: (id: string, body: RespondPractice) =>
    call<{ ok: true; assistantText: string }>(`/api/practices/${id}/respond`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  editPracticeSession: (id: string, stepKey: string, text: string) =>
    call<{ ok: true }>(`/api/practices/${id}/sessions/${encodeURIComponent(stepKey)}`, {
      method: 'PATCH',
      body: JSON.stringify({ text }),
    }),
  resetPracticeSession: (id: string, stepKey: string) =>
    call<{ ok: true }>(`/api/practices/${id}/sessions/${encodeURIComponent(stepKey)}/reset`, {
      method: 'POST',
    }),
  exportPracticeUrl: (id: string) => {
    const base = import.meta.env.VITE_API_URL || '';
    return `${base}/api/practices/${id}/export?format=md`;
  },

  // Founder mode (gated server-side by persona='founder')
  decisionsCreate: (body: {
    headline: string;
    why?: string;
    alternatives?: string[];
    expectedOutcome?: string;
    confidence: number;
  }) => call<{ id: string; ok: true }>('/api/founder/decisions', { method: 'POST', body: JSON.stringify(body) }),
  decisionsList: () => call<{ decisions: Array<Record<string, unknown>> }>('/api/founder/decisions'),
  decisionsWeeklyReview: () =>
    call<{ review: string; count: number }>('/api/founder/decisions/review/weekly', { method: 'POST' }),

  identityOutputRitual: (body: { outputLines: string[]; identityLines: string[] }) =>
    call<{
      id: string;
      verdicts: Array<{ claim: string; classification: string; confidence: number; reason?: string }>;
    }>('/api/founder/identity-output/ritual', { method: 'POST', body: JSON.stringify(body) }),
  identityOutputTrend: () =>
    call<{ counts: Record<string, number>; total: number }>('/api/founder/identity-output/trend'),

  calendarManualPaste: (events: Array<{ start: string; end: string; allDay?: boolean; attendees?: number }>) =>
    call<{ ingested: number }>('/api/founder/calendar/manual', {
      method: 'POST',
      body: JSON.stringify({ events }),
    }),
  calendarSummary: () =>
    call<{
      days: Array<{ date: string; meetingCount: number; blockCount: number; minutesBooked: number }>;
      totalEvents: number;
    }>('/api/founder/calendar/summary'),

  timeAuditRun: (estimateText: string) =>
    call<{ audit: string; evidence: Record<string, unknown> }>('/api/founder/time-audit/run', {
      method: 'POST',
      body: JSON.stringify({ estimateText }),
    }),
};
